import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  Target,
  Heart,
  Shield,
  TrendingUp,
  Clock,
  Zap,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  staggerContainer,
  fadeInUp,
  floatingAnimation,
  floatingTransition,
  floatingAnimationAlt,
  floatingTransitionAlt,
  viewportOptions,
} from '@/lib/animations';

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-amber-50/30 to-slate-100" />

      {/* Geometric Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Floating Elements */}
      <motion.div
        className="absolute top-20 right-20 w-64 h-64 rounded-full bg-gradient-to-br from-amber-200/20 to-orange-200/20 blur-3xl"
        animate={floatingAnimation}
        transition={floatingTransition}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-gradient-to-br from-slate-200/30 to-blue-200/20 blur-3xl"
        animate={floatingAnimationAlt}
        transition={floatingTransitionAlt}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto text-center"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/5 border border-slate-200 text-slate-700 text-sm font-medium">
              <Heart className="w-4 h-4 text-rose-500" />
              Built by Real Estate Professionals
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeInUp}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6"
          >
            Democratizing{' '}
            <span className="relative">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600">
                Property Data
              </span>
              <motion.span
                className="absolute bottom-1 left-0 right-0 h-3 bg-amber-200/50 -z-0"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ originX: 0 }}
              />
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeInUp}
            className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed"
          >
            We believe everyone deserves access to comprehensive property insights. No
            subscriptions, no gatekeeping—just the data you need to make informed decisions.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// MISSION SECTION
// ============================================================================
const MissionSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="max-w-4xl mx-auto"
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
              Our Mission
            </span>
          </motion.div>

          <motion.blockquote variants={fadeInUp} className="relative">
            <div className="absolute -top-6 -left-4 text-8xl text-amber-200 font-serif leading-none select-none">
              "
            </div>
            <p className="relative z-10 text-2xl md:text-3xl lg:text-4xl font-display font-medium text-slate-800 leading-relaxed text-center">
              We're on a mission to make real estate data accessible to everyone—from first-time
              homebuyers to seasoned investors. No industry connections required.
            </p>
            <div className="absolute -bottom-10 -right-4 text-8xl text-amber-200 font-serif leading-none select-none rotate-180">
              "
            </div>
          </motion.blockquote>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// WHY CHOOSE US SECTION
// ============================================================================
const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description:
        'Process thousands of properties in minutes, not hours. Our optimized pipeline delivers results at unprecedented speed.',
      color: 'amber',
    },
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description:
        'Your data is encrypted end-to-end. We never store your property lists longer than necessary for processing.',
      color: 'emerald',
    },
    {
      icon: Target,
      title: 'Precision Data',
      description:
        'Over 100 data points per property with 99.2% accuracy. Get the insights that matter for your decisions.',
      color: 'blue',
    },
    {
      icon: TrendingUp,
      title: 'Pay As You Go',
      description:
        'No subscriptions, no monthly fees. Simple per-row pricing means you only pay for what you actually use.',
      color: 'purple',
    },
  ];

  const colorClasses = {
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
  };

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm font-medium mb-4"
          >
            Why PropertyBulk
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-5xl font-bold text-slate-900 mb-4"
          >
            Built Different
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-slate-600 max-w-2xl mx-auto">
            We've rethought property data from the ground up
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.shallow}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {features.map((feature, index) => {
            const colors = colorClasses[feature.color];
            return (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all duration-300"
              >
                {/* Icon */}
                <div
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${colors.bg} ${colors.border} border mb-6`}
                >
                  <feature.icon className={`w-7 h-7 ${colors.text}`} />
                </div>

                <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// STATS SECTION
// ============================================================================
const StatsSection = () => {
  const stats = [
    { value: '2.5M+', label: 'Properties Processed', icon: Building2 },
    { value: '15,000+', label: 'Hours Saved', icon: Clock },
    { value: '99.2%', label: 'Data Accuracy', icon: Target },
    { value: '2,500+', label: 'Happy Customers', icon: Users },
  ];

  return (
    <section className="py-16 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((stat, index) => (
            <motion.div key={index} variants={fadeInUp} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500/10 mb-4">
                <stat.icon className="w-6 h-6 text-amber-500" />
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// VALUES SECTION
// ============================================================================
const ValuesSection = () => {
  const values = [
    {
      icon: CheckCircle2,
      title: 'Transparency',
      description: 'Clear pricing, no hidden fees. What you see is what you pay.',
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'Your data is yours. We process it and delete it—no exceptions.',
    },
    {
      icon: Heart,
      title: 'User-Centric',
      description: 'Built by real estate professionals who understand your needs.',
    },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.standard}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-sm font-medium mb-4"
          >
            Our Commitment
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4"
          >
            Values We Live By
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.shallow}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {values.map((value, index) => (
            <motion.div key={index} variants={fadeInUp} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 mb-6">
                <value.icon className="w-8 h-8 text-slate-700" />
              </div>
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">
                {value.title}
              </h3>
              <p className="text-slate-600 leading-relaxed max-w-sm mx-auto">{value.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
const CTASection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions.immediate}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-6"
          >
            Ready to Get Started?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
            Join thousands of real estate professionals who trust PropertyBulk for their data needs.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
            <Link to="/">
              <Button className="group h-14 px-8 text-lg font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300">
                Start Uploading
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                className="h-14 px-8 text-lg font-semibold border-2 border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-xl transition-all duration-300"
              >
                Contact Us
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// FOOTER
// ============================================================================
const Footer = () => {
  return (
    <footer className="py-12 bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-white">PropertyBulk</span>
          </div>

          <div className="flex items-center gap-8 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
            <Link to="/contact" className="hover:text-white transition-colors">
              Contact
            </Link>
          </div>

          <div className="text-sm text-slate-500">© 2024 PropertyBulk. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// MAIN ABOUT COMPONENT
// ============================================================================
const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <MissionSection />
      <WhyChooseUsSection />
      <StatsSection />
      <ValuesSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default About;
